import { defineMessages } from 'react-intl';
import type { Observable } from 'rxjs';
import { useIntl } from '../../hooks';
import type { MarketDataSnapshot } from '../../types/MarketDataSnapshot';
import type { OHLCData } from '../../types/OHLCData';
import type { ResolutionEnumNoPrefix } from '../../types/OrderAnalytic';
import type { Security } from '../../types/Security';
import type { ColDefIntl } from '../BlotterTable';
import type { BoxProps } from '../Core';
import { Sparkline } from './Sparkline';
import { TopOfBook, type TopOfBookProps } from './TopOfBook';
import { MarketViewWrapper } from './styles';
import type { ClickRowCallback } from './types';

export const MarketView = ({
  onClickRow,
  resolution,
  marketDataObservable,
  sparklineDataObservable,
  security,
  showSentiment = true,
  showSpread,
  showReferenceLines = true,
  width,
  bpsIncrement,
  usePreciseTopOfBookIncrement,
  ...boxProps
}: MarketViewProps) => {
  const intl = useIntl();
  const { description, limit } = getLimitAndDescription(resolution, intl);

  return (
    <MarketViewWrapper {...boxProps}>
      <TopOfBook
        onClickRow={onClickRow}
        topOfBookObservable={marketDataObservable}
        security={security}
        showSentiment={showSentiment}
        showSpread={showSpread}
        width={width}
        bpsIncrement={bpsIncrement}
        usePreciseTopOfBookIncrement={usePreciseTopOfBookIncrement}
      />
      <Sparkline
        key={security?.Symbol}
        description={description}
        dataObservable={sparklineDataObservable}
        limit={limit}
        security={security}
        showReferenceLines={showReferenceLines}
      />
    </MarketViewWrapper>
  );
};

export type MarketViewProps = {
  resolution: ResolutionEnumNoPrefix;
  onClickRow: ClickRowCallback;
  security: Security | undefined;
  marketDataObservable: Observable<MarketDataSnapshot>;
  sparklineDataObservable: Observable<OHLCData[]>;
  showSentiment?: boolean;
  showSpread?: boolean;
  showReferenceLines?: boolean;
  width?: number;
  bpsIncrement?: string;
  usePreciseTopOfBookIncrement?: TopOfBookProps['usePreciseTopOfBookIncrement'];
} & Omit<BoxProps, 'security'>;

const resolutionDescriptions = defineMessages({
  last24Hours: {
    id: 'VolumeLadder.last24Hours',
    defaultMessage: 'Last 24 hours',
  },
  last6Hours: {
    id: 'VolumeLadder.last6Hours',
    defaultMessage: 'Last 6 hours',
  },
  lastHour: {
    id: 'VolumeLadder.lastHour',
    defaultMessage: 'Last hour',
  },
  lastMinute: {
    id: 'VolumeLadder.lastMinute',
    defaultMessage: 'Last minute',
  },
});

export const getLimitAndDescription = (resolution: ResolutionEnumNoPrefix, intl: ColDefIntl) => {
  let description = '';
  let limit = 0;
  switch (resolution) {
    case '1H': {
      description = intl.formatMessage(resolutionDescriptions['last24Hours']);
      limit = 24;
      break;
    }
    case '5M': {
      description = intl.formatMessage(resolutionDescriptions['last6Hours']);
      limit = 72;
      break;
    }
    case '1M': {
      description = intl.formatMessage(resolutionDescriptions['lastHour']);
      limit = 60;
      break;
    }
    case '1S': {
      description = intl.formatMessage(resolutionDescriptions['lastMinute']);
      limit = 60;
      break;
    }
    default: {
      description = '';
      limit = 0;
      break;
    }
  }

  return { description, limit };
};
